import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Percent, Smartphone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PageHead } from "@/components/pages/PageShell";

const float = (delay) => ({
  initial: { opacity: 0, y: 18, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const TrailMock = () => {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <div className="relative mx-auto w-full max-w-md aspect-[4/5]" data-testid="supporter-hero-visual">
      <motion.div {...float(0.1)} className="absolute inset-x-4 top-6 bottom-10 journal-card bg-paper/80 overflow-hidden">
        <svg viewBox="0 0 300 380" className="absolute inset-0 w-full h-full" aria-hidden="true">
          <path d="M20 360 C 90 300, 40 240, 120 210 S 230 150, 200 90 S 250 30, 290 20" fill="none" stroke="#6D7C5E" strokeWidth="5" strokeDasharray="2 12" strokeLinecap="round" />
          <path d="M0 120 Q 80 140 150 118 T 300 132" fill="none" stroke="#8FA6B5" strokeWidth="18" opacity="0.18" />
        </svg>
      </motion.div>
      <motion.img
        {...float(0.35)}
        src="/trolls/kremmer-kari.webp"
        alt=""
        className="absolute inset-x-0 mx-auto bottom-6 h-[68%] w-auto drop-shadow-[0_10px_18px_rgba(46,42,37,0.28)]"
      />
      <motion.div {...float(0.6)} className="absolute right-0 top-14 inline-flex items-center gap-2 rounded-full bg-rowan text-parchment px-4 py-2 text-sm font-extrabold shadow-journal-lg">
        <MapPin className="w-4 h-4" />
        {s.mockPin}
      </motion.div>
      <motion.div {...float(0.8)} className="absolute left-0 top-[16%] inline-flex items-center gap-2 rounded-full bg-[#FF5B24] text-white px-4 py-2 text-sm font-extrabold shadow-journal-lg">
        <Smartphone className="w-4 h-4" />
        {s.mockVipps}
      </motion.div>
      <motion.div {...float(1)} className="absolute right-4 bottom-16 rounded-full bg-parchment border border-line px-4 py-2 text-xs font-extrabold text-ink shadow-journal">
        {s.mockNear}
      </motion.div>
    </div>
  );
};

export default function SupporterHero() {
  const { t } = useLang();
  const s = t.pages.supporter;
  return (
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <PageHead kicker={s.kicker} title={s.title} lead={s.lead}>
          <div data-testid="supporter-launch-badge" className="mt-7 inline-flex items-center gap-2 rounded-full bg-rowan/10 border border-rowan/30 px-4 py-2 text-sm font-extrabold text-rowan">
            <Percent className="w-4 h-4" />
            {s.launchBadge}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={{ hash: "#signup" }} data-testid="supporter-hero-signup" className="inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-7 py-3.5 font-extrabold shadow-journal transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
              {t.pages.signup.launchCta}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#packages" data-testid="supporter-see-packages" className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-7 py-3 font-extrabold text-ink hover:bg-ink hover:text-parchment transition-colors">
              {s.ctaPackages}
            </a>
          </div>
        </PageHead>
      </div>
      <div className="lg:col-span-5">
        <TrailMock />
      </div>
    </div>
  );
}
